import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isAssistantScopeReleased } from '@aica/api-types/schemas';
import type { AssistantTopic } from '@/contexts/AssistantDrawerContext';
import { useScreenSubject } from '@/contexts/ScreenSubjectContext';
import { decideScreen, isGatedScreen } from '@/lib/route-permissions';

/**
 * The whole base as a drawer topic, and why there is none:
 *
 * - `topic` — `null` until the contour is released, and for a subject the
 *   `assistant` screen is refused to;
 * - `refused` — the second case: the contour is out, and the subject's access
 *   refuses the whole base
 *   (`decideScreen`, #1645 — every grant holder). The API refuses it the
 *   whole base and the `/assistant` page bounces it, so no entry point may
 *   lead there, and the scope switcher says why instead of «coming soon»
 *   (review #1678). A drawer already open on it is left by the drawer
 *   provider, which decides what the access lets the assistant ask.
 *
 * Asked of the subject of either shell (`useScreenSubject`): the portal's
 * PermissionProvider, or the Bitrix24 iframe's session role and its own
 * `/access/me` (review #1678 — before, the iframe offered the whole base to a
 * grant holder and the API refused it).
 */
export interface OrgTopicState {
  topic: AssistantTopic | null;
  refused: boolean;
}

export function useOrgTopicState(): OrgTopicState {
  const { t } = useTranslation('assistant');
  const label = t('scope.org');
  const subject = useScreenSubject();
  const released = isAssistantScopeReleased('org');
  // Only a decision the gate makes refuses it: the `assistant` screen is open
  // to every role, so without a grant — even with a role not resolved yet in
  // the iframe — the whole base stays offered, as before E1.5. A contour not
  // released is refused to nobody: it is not out yet.
  const refused =
    released &&
    subject !== null &&
    isGatedScreen('assistant', subject.access) &&
    decideScreen('assistant', subject) === 'denied';
  return useMemo(
    () => ({
      topic: released && !refused ? { subject: { scope: 'org' }, label } : null,
      refused,
    }),
    [label, released, refused]
  );
}

/**
 * The whole base as a drawer topic (#765 Ф3) — for every entry point that
 * opens it: the scope switcher, the app header button with no record on
 * screen, the command palette, the Bitrix24 header. One construction, so the
 * four cannot name it differently. `null` when there is nothing to open
 * (`useOrgTopicState`); every entry point treats it as such.
 *
 * No filters: the drawer asks about the whole base as it is. A narrowed
 * question is asked from the `/assistant` page.
 */
export function useOrgTopic(): AssistantTopic | null {
  return useOrgTopicState().topic;
}
